import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { DAYS, LEVEL_META, waLink, ageLabel } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Search, Loader2, UserPlus, Trash2, Pencil, MessageCircle, KeyRound, Power } from "lucide-react";
import { PhotoUpload, Avatar } from "@/components/PhotoUpload";
import { toast } from "sonner";

const emptyStudent = {
  name: "", level: "TPA", class_label: "", gender: "L", birth_place: "", birth_date: "",
  address: "", father_name: "", mother_name: "", wa: "", photo: "", notes: "",
  monthly_fee: 150000, status: "aktif",
  schedule: [{ day: "Senin", time: "15:00" }, { day: "Rabu", time: "15:00" }, { day: "Jumat", time: "15:00" }],
};

function StudentDialog({ open, onOpenChange, initial, onSaved }) {
  const [form, setForm] = useState(emptyStudent);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      const base = initial ? { ...emptyStudent, ...initial } : emptyStudent;
      const sched = (base.schedule && base.schedule.length ? base.schedule : emptyStudent.schedule).slice(0, 3);
      while (sched.length < 3) sched.push({ day: "Senin", time: "15:00" });
      setForm({ ...base, schedule: sched });
    }
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSlot = (i, k, v) => setForm((f) => {
    const s = [...f.schedule]; s[i] = { ...s[i], [k]: v }; return { ...f, schedule: s };
  });

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nama siswa wajib diisi");
    setSaving(true);
    try {
      const payload = { ...form, monthly_fee: Number(form.monthly_fee) || 150000 };
      if (initial?.id) await api.put(`/students/${initial.id}`, payload);
      else await api.post("/students", payload);
      toast.success(initial ? "Data siswa diperbarui" : "Siswa baru terdaftar 🎉");
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto gemma-scroll rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{initial ? "Edit Siswa" : "Pendaftaran Siswa Baru"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="flex items-center gap-4">
            <PhotoUpload value={form.photo} onChange={(v) => set("photo", v)} fallback={(form.name || "?").charAt(0)} testId="student-photo-upload" />
            <div className="text-sm text-slate-500">Klik untuk unggah foto siswa<br /><span className="text-xs text-slate-400">JPG/PNG/WebP, maks 3 MB</span></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Nama Lengkap</Label>
              <Input data-testid="student-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1 rounded-xl" />
            </div>
            <div>
              <Label>Jenjang</Label>
              <Select value={form.level} onValueChange={(v) => set("level", v)}>
                <SelectTrigger data-testid="student-level-select" className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="TPA">TPA</SelectItem>
                  <SelectItem value="TK">TK</SelectItem>
                  <SelectItem value="SD">SD</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Kelas / Tingkat</Label>
              <Input value={form.class_label} onChange={(e) => set("class_label", e.target.value)} placeholder="cth: SD Kelas 2 / Iqro 3" className="mt-1 rounded-xl" />
            </div>
            <div>
              <Label>Jenis Kelamin</Label>
              <Select value={form.gender} onValueChange={(v) => set("gender", v)}>
                <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="L">Laki-laki</SelectItem>
                  <SelectItem value="P">Perempuan</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tempat Lahir</Label>
              <Input value={form.birth_place} onChange={(e) => set("birth_place", e.target.value)} className="mt-1 rounded-xl" />
            </div>
            <div>
              <Label>Tanggal Lahir</Label>
              <Input type="date" value={form.birth_date} onChange={(e) => set("birth_date", e.target.value)} className="mt-1 rounded-xl" />
            </div>
          </div>
          <div>
            <Label>Alamat</Label>
            <Textarea value={form.address} onChange={(e) => set("address", e.target.value)} className="mt-1 rounded-xl" rows={2} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Nama Ayah</Label>
              <Input value={form.father_name} onChange={(e) => set("father_name", e.target.value)} className="mt-1 rounded-xl" />
            </div>
            <div>
              <Label>Nama Ibu</Label>
              <Input value={form.mother_name} onChange={(e) => set("mother_name", e.target.value)} className="mt-1 rounded-xl" />
            </div>
            <div className="sm:col-span-2">
              <Label>No. WhatsApp Wali</Label>
              <Input data-testid="student-wa-input" value={form.wa} onChange={(e) => set("wa", e.target.value)} placeholder="08xxx atau 628xxx" className="mt-1 rounded-xl" />
            </div>
          </div>

          <div>
            <Label className="mb-2 block">Jadwal Les (3 hari/minggu · durasi 1 jam)</Label>
            <div className="space-y-2">
              {form.schedule.map((s, i) => (
                <div key={i} className="flex gap-2">
                  <Select value={s.day} onValueChange={(v) => setSlot(i, "day", v)}>
                    <SelectTrigger data-testid={`schedule-day-${i}`} className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>{DAYS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="time" data-testid={`schedule-time-${i}`} value={s.time} onChange={(e) => setSlot(i, "time", e.target.value)} className="rounded-xl" />
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Iuran per Bulan (Rp)</Label>
              <Input type="number" value={form.monthly_fee} onChange={(e) => set("monthly_fee", e.target.value)} className="mt-1 rounded-xl" />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="student-status-select" className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="aktif">Aktif — jadwal berjalan</SelectItem>
                  <SelectItem value="nonaktif">Nonaktif — berhenti sementara</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Catatan Khusus Belajar</Label>
            <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} className="mt-1 rounded-xl" rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-xl">Batal</Button>
          <Button data-testid="save-student-btn" onClick={save} disabled={saving} className="rounded-xl bg-orange-500 hover:bg-orange-600">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ParentDialog({ open, onOpenChange, student }) {
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open && student) setForm({ name: `Wali ${student.name}`, email: "", password: "" }); }, [open, student]);

  const save = async () => {
    if (!form.email || !form.password) return toast.error("Email & sandi wajib diisi");
    setSaving(true);
    try {
      await api.post("/parents", { ...form, student_id: student.id });
      toast.success("Akun wali murid dibuat 🎉");
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl">
        <DialogHeader><DialogTitle className="font-display text-xl">Buat Akun Wali · {student?.name}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label>Nama Wali</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-xl" /></div>
          <div><Label>Email Login</Label><Input data-testid="parent-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 rounded-xl" /></div>
          <div><Label>Kata Sandi</Label><Input data-testid="parent-password-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 rounded-xl" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-xl">Batal</Button>
          <Button data-testid="save-parent-btn" onClick={save} disabled={saving} className="rounded-xl bg-sky-600 hover:bg-sky-700">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Buat Akun"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Students() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [level, setLevel] = useState("all");
  const [statusF, setStatusF] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [parentFor, setParentFor] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (level !== "all") params.level = level;
    if (statusF !== "all") params.status = statusF;
    api.get("/students", { params }).then((r) => setStudents(r.data)).finally(() => setLoading(false));
  }, [q, level, statusF]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const toggleStatus = async (s) => {
    const next = s.status === "aktif" ? "nonaktif" : "aktif";
    await api.put(`/students/${s.id}`, { status: next });
    toast.success(next === "aktif" ? `${s.name} diaktifkan kembali, jadwal berjalan lagi 🎉` : `${s.name} dinonaktifkan, jadwal dihentikan`);
    load();
  };

  const remove = async (s) => {
    if (!window.confirm(`Hapus data ${s.name}? Semua riwayat ikut terhapus.`)) return;
    await api.delete(`/students/${s.id}`);
    toast.success("Data siswa dihapus");
    load();
  };

  return (
    <div className="space-y-6 animate-float-in">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Data Siswa</h1>
          <p className="text-slate-500 mt-1">{students.length} siswa terdaftar</p>
        </div>
        <Button data-testid="add-student-btn" onClick={() => { setEditing(null); setDialogOpen(true); }}
          className="rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 shadow-md shadow-orange-200">
          <Plus className="w-4 h-4 mr-1" /> Siswa Baru
        </Button>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="student-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama siswa..." className="pl-9 rounded-xl border-amber-200" />
        </div>
        <Select value={level} onValueChange={setLevel}>
          <SelectTrigger data-testid="student-level-filter" className="w-40 rounded-xl border-amber-200"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Jenjang</SelectItem>
            <SelectItem value="TPA">TPA</SelectItem>
            <SelectItem value="TK">TK</SelectItem>
            <SelectItem value="SD">SD</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusF} onValueChange={setStatusF}>
          <SelectTrigger data-testid="student-status-filter" className="w-40 rounded-xl border-amber-200"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Status</SelectItem>
            <SelectItem value="aktif">Aktif</SelectItem>
            <SelectItem value="nonaktif">Nonaktif</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>
      ) : students.length === 0 ? (
        <Card className="p-12 text-center rounded-2xl border-amber-100 text-slate-400">Belum ada siswa. Klik "Siswa Baru".</Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {students.map((s) => (
            <Card key={s.id} data-testid={`student-card-${s.id}`} className={`p-5 rounded-2xl border-amber-100 hover:shadow-md transition-shadow ${s.status !== "aktif" ? "opacity-75 bg-slate-50" : ""}`}>
              <div className="flex items-start justify-between gap-2">
                <button onClick={() => navigate(`/siswa/${s.id}`)} className="flex items-center gap-3 text-left min-w-0">
                  <Avatar src={s.photo} name={s.name} />
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate hover:text-orange-600 transition-colors">{s.name}</div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <Badge variant="outline" className={`text-[11px] rounded-full ${LEVEL_META[s.level]?.cls}`}>{s.level}</Badge>
                      {s.class_label && <span className="text-xs text-slate-400">{s.class_label}</span>}
                      {ageLabel(s.birth_date) && <span data-testid={`student-age-${s.id}`} className="text-xs text-slate-500 font-medium">· {ageLabel(s.birth_date)}</span>}
                    </div>
                  </div>
                </button>
                <Badge data-testid={`student-status-${s.id}`} className={`rounded-full border-0 text-[11px] shrink-0 ${s.status === "aktif" ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>{s.status === "aktif" ? "Aktif" : "Nonaktif"}</Badge>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {s.status !== "aktif" ? <span className="text-[11px] text-slate-400 italic">Tidak ada jadwal aktif</span> :
                (s.schedule || []).map((sl, i) => (
                  <span key={i} className="text-[11px] px-2 py-1 rounded-lg bg-sky-50 text-sky-700 font-medium">{sl.day} {sl.time}</span>
                ))}
              </div>
              <div className="mt-4 flex items-center gap-1.5 border-t border-amber-50 pt-3">
                <Button size="sm" variant="ghost" data-testid={`wa-student-${s.id}`}
                  onClick={() => window.open(waLink(s.wa, `Assalamu'alaikum Bapak/Ibu wali dari ${s.name} 🌟`), "_blank")}
                  className="rounded-lg text-emerald-600 hover:bg-emerald-50 h-8 px-2"><MessageCircle className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => setParentFor(s)} className="rounded-lg text-sky-600 hover:bg-sky-50 h-8 px-2" title="Buat akun wali"><KeyRound className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" data-testid={`edit-student-${s.id}`} onClick={() => { setEditing(s); setDialogOpen(true); }} className="rounded-lg text-slate-600 hover:bg-slate-100 h-8 px-2"><Pencil className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" data-testid={`toggle-status-${s.id}`} onClick={() => toggleStatus(s)} title={s.status === "aktif" ? "Nonaktifkan" : "Aktifkan kembali"} className={`rounded-lg h-8 px-2 ${s.status === "aktif" ? "text-amber-600 hover:bg-amber-50" : "text-emerald-600 hover:bg-emerald-50"}`}><Power className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" data-testid={`delete-student-${s.id}`} onClick={() => remove(s)} className="rounded-lg text-rose-600 hover:bg-rose-50 h-8 px-2 ml-auto"><Trash2 className="w-4 h-4" /></Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <StudentDialog open={dialogOpen} onOpenChange={setDialogOpen} initial={editing} onSaved={load} />
      <ParentDialog open={!!parentFor} onOpenChange={(v) => !v && setParentFor(null)} student={parentFor} />
    </div>
  );
}
